import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../../../config/api';
import AdminNavbar from '../components/AdminNavbar';
import Sidebar from '../components/Sidebar';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  CardMedia
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const toggleDrawer = () => setSidebarOpen(!sidebarOpen);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await ApiService.get(`/blogs/read/${id}`);
        if (response.data.error) {
          throw new Error(response.data.message);
        }
        setBlog(response.data.meta);
      } catch (err) {
        setError('Failed to fetch blog details.');
        console.error(err);
      }
    };
    fetchBlog();
  }, [id]);

  const handleEdit = () => {
    navigate(`/admin/create-blog/${id}`);
  };

  const handleDelete = async () => {
    try {
      await ApiService.delete(`/blogs/remove/${id}`);
      setSnackbar({ open: true, message: 'Blog deleted successfully!', severity: 'success' });
      setTimeout(() => navigate('/admin/blogs'), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete blog.', severity: 'error' });
      console.error(err);
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'var(--bs-accent)' }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  if (!blog) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--bs-secondary)' }}>
        <CircularProgress sx={{ color: 'var(--bs-primary)' }} />
      </Box>
    );
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        backgroundColor: 'var(--bs-secondary)',
        minHeight: '100vh',
      }}
    >
      <Sidebar open={sidebarOpen} toggleDrawer={toggleDrawer} setOpen={setSidebarOpen} />
      <Box>
        <AdminNavbar />
        <Container maxWidth="md" sx={{ py: 5 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/blogs')}
              sx={{ color: 'var(--bs-primary)', mb: 2 }}
            >
              Back to Blogs
            </Button>
          </Box>
          
          <Paper elevation={3} sx={{ p: 4, backgroundColor: 'var(--bs-primary)', color: 'var(--bs-accent)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Typography variant="h4" component="h1" sx={{ color: 'var(--bs-accent)', fontWeight: 'bold' }}>
                {blog.title}
              </Typography>
              
              <Box>
                <IconButton 
                  onClick={handleEdit}
                  sx={{ 
                    color: 'var(--bs-success)', 
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    mr: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  onClick={() => setDeleteConfirmOpen(true)}
                  sx={{ 
                    color: 'var(--bs-danger)', 
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.2)',
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ my: 3, textAlign: 'center', borderRadius: '8px', overflow: 'hidden' }}>
              <CardMedia
                component="img"
                src={blog.image}
                alt={blog.title}
                sx={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
              />
            </Box>
            
            <Box
              sx={{ 
                lineHeight: 1.8, 
                fontSize: '1.1rem',
                '& p': { mb: 2 },
                '& img': { maxWidth: '100%', height: 'auto' }
              }}
              dangerouslySetInnerHTML={{ __html: blog.description }}
            />
            
            <Box sx={{ 
              mt: 4, 
              p: 2, 
              backgroundColor: 'rgba(0,0,0,0.05)', 
              borderRadius: '8px',
              fontSize: '0.9rem', 
              color: 'var(--bs-accent)' 
            }}>
              <Typography variant="body2" sx={{ my: 0.5 }}>
                <strong style={{ color: 'var(--bs-accent)' }}>Created By:</strong> {blog.createdBy}
              </Typography>
              <Typography variant="body2" sx={{ my: 0.5 }}>
                <strong style={{ color: 'var(--bs-accent)' }}>Created At:</strong>{' '}
                {new Date(blog.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
              <Typography variant="body2" sx={{ my: 0.5 }}>
                <strong style={{ color: 'var(--bs-accent)' }}>Updated At:</strong>{' '}
                {new Date(blog.updatedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this blog? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogDetail;